import { Box } from '@lucidhq/lucidium';
import { ReactElement, useEffect, useState } from 'react';

import {
  OptionInput,
  OptionItem,
  OptionItemLabel,
  SwitchSelectorProps,
  SwitchSelectorWrapper,
} from '@/components';

const CLASS_NAMES_PREFIX = 'react-switch-selector';

const defaultColors = {
  backgroundColor: '#6942B2',
  selectedBackgroundColor: '#ffffff',
  fontColor: '#ffffff',
  selectedFontColor: '#000000',
};

export const SwitchSelector = (props: SwitchSelectorProps) => {
  const {
    onChange = (): void => {},
    options = [],
    initialSelectedIndex = 0,
    height = 30,
    width = 145,
    testId,
  } = props;
  const canApplyInitialSelectedIndex = !!options[initialSelectedIndex];
  const [selectedIndex, setSelectedIndex] = useState(
    canApplyInitialSelectedIndex ? initialSelectedIndex : 0
  );

  const {
    $border = 0,
    $backgroundColor = defaultColors.backgroundColor,
    $selectedBackgroundColor = defaultColors.selectedBackgroundColor,
    $wrapperBorderRadius = 20,
    $optionBorderRadius = 18,
    fontSize = 14,
    $fontColor = defaultColors.fontColor,
    $selectedFontColor = defaultColors.selectedFontColor,
    $selectionIndicatorMargin = 3,
    forcedSelectedIndex,
    disabled = false,
    name,
  } = props;

  useEffect(() => {
    if (
      forcedSelectedIndex !== undefined &&
      !!options[forcedSelectedIndex] &&
      forcedSelectedIndex !== selectedIndex
    ) {
      setSelectedIndex(forcedSelectedIndex);
    }
  }, [forcedSelectedIndex, options, selectedIndex]);

  const handleOnClick = (index: number): void => {
    if (!disabled && index !== selectedIndex) {
      setSelectedIndex(index);
      onChange(options[index].value);
    }
  };

  const renderOptions = (): ReactElement[] => {
    return options.map((option, index) => {
      const isSelected = selectedIndex === index;
      const optionId = `${name ?? 'rss'}-option-${index}`;
      const isRawText = typeof option.label === 'string';

      const labelRawTextProps = {
        fontSize,
        $fontColor: option.fontColor || $fontColor,
        $selectedFontColor: option.selectedFontColor || $selectedFontColor,
      };

      return (
        <OptionItem
          key={optionId}
          $optionsAmount={options.length}
          className={`${CLASS_NAMES_PREFIX}-option ${CLASS_NAMES_PREFIX}-option-${
            isSelected ? 'selected' : 'unselected'
          }`}
          $optionBorderRadius={$optionBorderRadius}
        >
          <OptionItemLabel
            className={`${CLASS_NAMES_PREFIX}-option-label`}
            selected={isSelected}
            $isRawText={isRawText}
            disabled={disabled}
            aria-disabled={disabled}
            htmlFor={optionId}
            {...(isRawText ? labelRawTextProps : {})}
          >
            <OptionInput
              type="radio"
              id={optionId}
              name={name}
              onChange={(): void => handleOnClick(index)}
              checked={isSelected}
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
            />
            {option.label}
          </OptionItemLabel>
        </OptionItem>
      );
    });
  };

  if (!options.length) {
    return null;
  }

  return (
    <Box lss={{ height, width }}>
      <SwitchSelectorWrapper
        $selectedIndex={selectedIndex}
        $optionsAmount={options.length}
        className={`${CLASS_NAMES_PREFIX}-wrapper ${
          disabled ? `${CLASS_NAMES_PREFIX}-disabled` : ''
        }`}
        $border={$border}
        $backgroundColor={$backgroundColor}
        $selectedBackgroundColor={
          options[selectedIndex]?.selectedBackgroundColor ||
          $selectedBackgroundColor
        }
        $wrapperBorderRadius={$wrapperBorderRadius}
        $optionBorderRadius={$optionBorderRadius}
        $selectionIndicatorMargin={$selectionIndicatorMargin}
        disabled={disabled}
        role={'radiogroup'}
        aria-labelledby={name}
        data-testId={testId}
      >
        {renderOptions()}
      </SwitchSelectorWrapper>
    </Box>
  );
};
